import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Check } from 'lucide-react';
import { Spinner } from './LoadingIndicator';

/**
 * Reusable Input component with glassmorphism styling
 * Supports labels, helper text, error states, and icons
 */
const Input = forwardRef(({
  label,
  helperText,
  error,
  icon: Icon,
  iconPosition = 'left',
  validating = false,
  validated = false,
  className = '',
  containerClassName = '',
  fullWidth = true,
  ...props
}, ref) => {

  // Base input styles
  const baseStyles = 'glass rounded-lg px-4 py-2 text-white placeholder-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50 disabled:opacity-50 disabled:cursor-not-allowed';

  // Border styles based on state
  const getBorderStyles = () => {
    if (error) return 'border-error-500 focus:border-error-500';
    if (validating) return 'border-primary-500 focus:border-primary-500';
    if (validated) return 'border-success-500 focus:border-success-500';
    return 'focus:border-primary-500/50';
  };

  const borderStyles = getBorderStyles();

  // Icon padding adjustments - account for validation indicators on right
  const hasRightIndicator = validating || validated;
  const iconPaddingStyles = Icon
    ? iconPosition === 'left' ? 'pl-11' : 'pr-11'
    : '';
  const validationPaddingStyles = hasRightIndicator ? 'pr-10' : '';

  // Width styles
  const widthStyle = fullWidth ? 'w-full' : '';

  // Combine input styles
  const inputClasses = `${baseStyles} ${borderStyles} ${iconPaddingStyles} ${validationPaddingStyles} ${widthStyle} ${className}`;

  return (
    <div className={`${fullWidth ? 'w-full' : ''} ${containerClassName}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {label}
        </label>
      )}

      <div className="relative">
        {Icon && iconPosition === 'left' && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <Icon size={18} aria-hidden="true" />
          </div>
        )}

        <input
          ref={ref}
          className={inputClasses}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error ? `${props.id}-error` : helperText ? `${props.id}-helper` : undefined
          }
          {...props}
        />

        {Icon && iconPosition === 'right' && !hasRightIndicator && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <Icon size={18} aria-hidden="true" />
          </div>
        )}

        {/* Validating indicator */}
        {validating && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-500 pointer-events-none">
            <Spinner size="sm" />
          </div>
        )}

        {/* Validated indicator */}
        {validated && !validating && !error && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-success-500 pointer-events-none">
            <Check size={18} aria-hidden="true" />
          </div>
        )}
      </div>

      {error && (
        <p
          id={`${props.id}-error`}
          className="mt-1.5 text-sm text-error-400"
          role="alert"
        >
          {error}
        </p>
      )}

      {!error && helperText && (
        <p
          id={`${props.id}-helper`}
          className="mt-1.5 text-sm text-gray-400"
        >
          {helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

Input.propTypes = {
  label: PropTypes.string,
  helperText: PropTypes.string,
  error: PropTypes.string,
  icon: PropTypes.elementType,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  validating: PropTypes.bool,
  validated: PropTypes.bool,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  fullWidth: PropTypes.bool,
  id: PropTypes.string,
};

/**
 * Textarea component with glassmorphism styling
 */
export const Textarea = forwardRef(({
  label,
  helperText,
  error,
  className = '',
  containerClassName = '',
  fullWidth = true,
  rows = 4,
  ...props
}, ref) => {

  // Base textarea styles
  const baseStyles = 'glass rounded-lg px-4 py-2 text-white placeholder-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50 disabled:opacity-50 disabled:cursor-not-allowed resize-vertical';

  // Border styles based on state
  const borderStyles = error
    ? 'border-error-500 focus:border-error-500'
    : 'focus:border-primary-500/50';

  // Width styles
  const widthStyle = fullWidth ? 'w-full' : '';

  // Combine textarea styles
  const textareaClasses = `${baseStyles} ${borderStyles} ${widthStyle} ${className}`;

  return (
    <div className={`${fullWidth ? 'w-full' : ''} ${containerClassName}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {label}
        </label>
      )}

      <textarea
        ref={ref}
        rows={rows}
        className={textareaClasses}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={
          error ? `${props.id}-error` : helperText ? `${props.id}-helper` : undefined
        }
        {...props}
      />

      {error && (
        <p
          id={`${props.id}-error`}
          className="mt-1.5 text-sm text-error-400"
          role="alert"
        >
          {error}
        </p>
      )}

      {!error && helperText && (
        <p
          id={`${props.id}-helper`}
          className="mt-1.5 text-sm text-gray-400"
        >
          {helperText}
        </p>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';

Textarea.propTypes = {
  label: PropTypes.string,
  helperText: PropTypes.string,
  error: PropTypes.string,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  fullWidth: PropTypes.bool,
  rows: PropTypes.number,
  id: PropTypes.string,
};

export default Input;
